import { useState } from 'react';

import { Navigate } from 'react-router-dom';

import { ManageMattersDialog } from './manage-matters-dialog';
import { ManagedUsersPanel } from './managed-users-panel';
import { Permission } from '../../../../../backend/src/auth/permissions/permission';
import { OrgPermission } from '../../../../../backend/src/organisation/organisation-permission';
import { User } from '@/api/types/backend-dto-types';
import { useUserContext } from '@/Contexts/User';
import { useOrgPermissions } from '@/hooks/orgPermissions';
import { useHasPermission } from '@/hooks/useHasPermission';
import { Breadcrumb } from '@/new/components/breadcrumb';
import { PageContainer } from '@/new/components/page-container';
import { PageHeader } from '@/new/components/page-header';
import { PageTitle } from '@/new/components/page-title';

export function ManageUsers() {
  const { user } = useUserContext();
  const { hasPermission } = useHasPermission();
  const { isEnabled: isOrgFeatureEnabled } = useOrgPermissions();

  if (!user) return null;
  if (!hasPermission(Permission.UserManage)) return <Navigate to="/matters" replace />;

  const [modalOpen, setModalOpen] = useState(false);
  const [selectedUserId, setSelectedUserId] = useState<string | null>(null);

  // The dialog lists every Matter in the organisation, and is only useful with at least one
  // of the membership permissions the backend enforces on add-user/remove-user
  const canManageUserMatters =
    hasPermission(Permission.MatterAccessAll) &&
    (hasPermission(Permission.MatterAddUsers) || hasPermission(Permission.MatterRemoveUsers));

  const handleOpenUserMattersModal = (selectedUser: User) => {
    if (!canManageUserMatters) return;
    setSelectedUserId(selectedUser.id);
    setModalOpen(true);
  };

  return (
    <>
      <Breadcrumb items={[{ href: '/manage-users', label: 'Manage Users', active: true }]} />
      <PageContainer>
        <PageHeader>
          <PageTitle text="Manage Users" />
        </PageHeader>
      </PageContainer>

      <ManagedUsersPanel
        organisationId={user.organisationId}
        onViewUserMatters={handleOpenUserMattersModal}
        showBlockControls={isOrgFeatureEnabled(OrgPermission.UserBlocking)}
        requiresExternalUser2fa={isOrgFeatureEnabled(OrgPermission.Require2FANonSSO)}
        externalUserSettingsHref="/organisation-settings/general#external-user-security"
      />

      <ManageMattersDialog
        userId={selectedUserId}
        organisationId={user.organisationId}
        open={modalOpen}
        onOpenChange={setModalOpen}
      />
    </>
  );
}
